// Layer 1 outreach attribution.
//
// Captures where a shopper arrived from — a cold-email `?ref`, UTM tags, or the
// referring host — once per session, so each prospect's funnel (open → upload →
// result → add-to-cart) is measurable. First-party only: parsed in the browser,
// sent as CommerceEvent `properties`, stored in our own event stream. No
// third-party tracker, no cookies, host-only referrer (no PII, no full URL).

export type Attribution = Record<string, string>

// Explicit, intentional tags win. `ref` is our cold-email prospect tag.
const TAG_KEYS = [
  "ref", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"
] as const

const VALUE_LIMIT = 128
const STORE_KEY = "vc_attribution"

// Pure: derive attribution from a query string and a referrer. No globals, so it
// is trivially unit-testable.
export function parseAttribution(search: string, referrer = ""): Attribution {
  const params = new URLSearchParams(search)
  const attribution: Attribution = {}

  for (const key of TAG_KEYS) {
    const value = params.get(key)
    if (value) attribution[key] = value.slice(0, VALUE_LIMIT)
  }

  // Only fall back to the referrer host when no explicit tag was given — an
  // explicit ?ref/utm is the stronger, intentional signal. Host only: never the
  // full referring URL (privacy), and only web schemes (android-app:/etc. are
  // opaque app identifiers, not attribution-worthy hosts).
  if (Object.keys(attribution).length === 0 && referrer) {
    try {
      const url = new URL(referrer)
      const host = url.host
      if (host && /^https?:$/.test(url.protocol)) attribution.referrer = host.slice(0, VALUE_LIMIT)
    } catch {
      // Opaque/empty referrer — nothing to record.
    }
  }

  return attribution
}

type AttributionWindow = {
  location: { search: string }
  document: { referrer: string }
  sessionStorage: Pick<Storage, "getItem" | "setItem">
}

// Sticky per session: the `?ref` is usually only on the landing URL, but every
// later event in the visit should stay attributed to it. Capture once, persist
// in sessionStorage, reuse for the rest of the session.
export function resolveAttribution(win: AttributionWindow): Attribution {
  try {
    const stored = win.sessionStorage.getItem(STORE_KEY)
    if (stored) return JSON.parse(stored) as Attribution
  } catch {
    // Storage blocked or corrupt value — fall through to a fresh parse.
  }

  const attribution = parseAttribution(win.location.search, win.document.referrer)

  if (Object.keys(attribution).length > 0) {
    try {
      win.sessionStorage.setItem(STORE_KEY, JSON.stringify(attribution))
    } catch {
      // Storage unavailable (private mode) — attribution still rides this page's
      // events; only cross-page stickiness is lost.
    }
  }

  return attribution
}
